import { useState } from "react";
import { Pressable } from "react-native";
import { useRouter, usePathname } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import { type Period } from "../utils/summarise-buckets";
import { PeriodDropdown } from "./PeriodDropdown.web";

interface ScenarioDetailPanelProps {
  scenarioUuid: string;
  scenarioName: string;
  cameraDisplayName: string;
}

export function ScenarioDetailPanel({ scenarioName, cameraDisplayName }: ScenarioDetailPanelProps) {
  const { theme } = useUnistyles();
  const router = useRouter();
  const pathname = usePathname();
  const [period, setPeriod] = useState<Period>("7d");

  const handleClose = () => router.replace(pathname as never);

  return (
    <View flex={1} gap="3xl">
      {/* Header */}
      <View flexDirection="row" justifyContent="space-between" alignItems="flex-start" gap="lg">
        <View flex={1} gap="xs">
          <Text textStyle="lg/bold" color="text" numberOfLines={2}>
            {scenarioName}
          </Text>
          <Text textStyle="sm/regular" color="textMuted" numberOfLines={1}>
            {cameraDisplayName}
          </Text>
        </View>
        <Pressable
          onPress={handleClose}
          accessibilityRole="button"
          accessibilityLabel="Close scenario details"
          style={{ padding: theme.spacing.xs }}
        >
          <Icon name="close" size={theme.spacing["5xl"]} color={theme.colors.icon} />
        </Pressable>
      </View>

      {/* Period control */}
      <View>
        <PeriodDropdown value={period} onChange={setPeriod} />
      </View>

      {/* Empty state — bucket fetch endpoint not wired yet (Tasks #41-43). */}
      <View
        flex={1}
        alignItems="center"
        justifyContent="center"
        style={{
          borderWidth: 1,
          borderColor: theme.colors.borderCard,
          borderRadius: theme.spacing.md,
          padding: theme.spacing["6xl"],
          gap: theme.spacing.lg,
        }}
      >
        <Icon name="motion" size={theme.spacing["7xl"]} color={theme.colors.textMuted} />
        <Text textStyle="md/bold" color="text">
          No traffic flow data available yet
        </Text>
        <Text textStyle="sm/regular" color="textMuted" numberOfLines={3} style={{ textAlign: "center" }}>
          This camera hasn&apos;t reported any traffic-flow counts for the selected period.
        </Text>
      </View>
    </View>
  );
}
